import {
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Typography,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  InputAdornment,
  Stack,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridRowModes,
} from "@mui/x-data-grid";
import { useState, useEffect, useCallback, useMemo } from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import KeyIcon from "@mui/icons-material/Key";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import toast, { Toaster } from "react-hot-toast";
import Swal from "sweetalert2";
import * as services from "./../../services/saleng";
import AppBreadcrumbs from "./../../components/AppBreadcrumbs";
import UserFormDialog from "./UserFormDialog";

function generateColorFromId(id) {
  const hue = (id * 137) % 360;
  return `hsl(${hue}, 70%, 80%)`;
}

// 🔐 REGEX: อนุญาตเฉพาะตัวอักษรและตัวเลข 8–32 ตัว ห้ามช่องว่าง/อักขระพิเศษ
const PASSWORD_REGEX = /^[A-Za-z0-9]{8,32}$/;

const Users = () => {
  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);

  // 🔑 state สำหรับ Reset Password
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const passwordError = useMemo(() => {
    if (!password) return "";
    if (!PASSWORD_REGEX.test(password))
      return "รหัสผ่านต้องเป็นตัวอักษร/ตัวเลข 8–32 ตัว (ห้ามมีช่องว่าง/อักขระพิเศษ)";
    return "";
  }, [password]);
  const confirmError = useMemo(() => {
    if (!confirm) return "";
    if (confirm !== password) return "ยืนยันรหัสผ่านไม่ตรงกัน";
    return "";
  }, [confirm, password]);

  const handleOpenReset = (row) => {
    setSelectedUser(row);
    setPassword("");
    setConfirm("");
    setShowPass(false);
    setShowConfirm(false);
    setOpenResetDialog(true);
  };

  const handleSubmitReset = async () => {
    if (!password || !confirm) {
      toast.error("กรอกข้อมูลให้ครบก่อนน้า");
      return;
    }
    if (passwordError || confirmError) {
      toast.error("ตรวจสอบรูปแบบรหัสผ่านอีกครั้งจ้า");
      return;
    }
    try {
      // เรียกใช้บริการ reset password ด้วย username
      await services.resetPassword(selectedUser.username, password);
      toast.success("เปลี่ยนรหัสผ่านสำเร็จ");
      setOpenResetDialog(false);
    } catch (err) {
      console.error("Reset password error:", err);
      toast.error("เปลี่ยนรหัสผ่านไม่สำเร็จ");
    }
  };

  const columns = [
    { field: "id", headerName: "ลำดับ", flex: 0, width: 70 },
    {
      field: "username",
      headerName: "ชื่อผู้ใช้งาน",
      flex: 1,
      editable: false,
    },
    { field: "name", headerName: "ชื่อ", flex: 1, editable: true },
    { field: "lastname", headerName: "นามสกุล", flex: 1, editable: true },
    {
      field: "branch",
      headerName: "สาขา",
      width: 200,
      display: "flex",
      align: "center",
      headerAlign: "center",
      alignContent: "center",
      sortable: false,
      filterable: false,
      flex: 0,
      renderCell: ({ id, row }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return (
            <FormControl sx={{ width: "100%" }}>
              <Select
                size="small" value={row?.branchId || ""}
                onChange={(event) =>
                  handleBranchChange(row.id, event.target.value)
                }
              >
                {branches.map((option) => (
                  <MenuItem key={option.key} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }

        const branch = branches.find((branch) => branch.value === row.branchId);

        return row.branchId && branch ? (
          <Chip
            sx={{
              backgroundColor: generateColorFromId(row.branchId),
            }}
            label={branch.label}
          />
        ) : null;
      },
    },
    {
      field: "role",
      headerName: "บทบาท",
      width: 200,
      display: "flex",
      align: "center",
      headerAlign: "center",
      alignContent: "center",
      sortable: false,
      filterable: false,
      flex: 0,
      renderCell: ({ id, row }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return (
            <FormControl sx={{ width: "100%" }}>
              <Select size="small"
                value={row.roleId || ""}
                onChange={(event) =>
                  setRows((prevRows) =>
                    prevRows.map((r) =>
                      r.id === id
                        ? {
                          ...r,
                          roleId: event.target.value,
                          roleId_old: r.roleId,
                        }
                        : r
                    )
                  )
                }
              >
                {roles.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }

        const role = roles.find((role) => role.id === row.roleId);
        return role ? (
          <Chip
            label={role.title}
            sx={{
              backgroundColor: generateColorFromId(role.id),
            }}
          />
        ) : null;
      },
    },
    {
      field: "isDisabled",
      headerName: "สถานะ",
      width: 150,
      headerAlign: "center",
      align: "center",
      renderCell: ({ row }) => {
        return (
          <FormControlLabel
            control={
              <Switch
                checked={!row.isDisabled}
                onChange={() => handleToggleDisabled(row)}
                color="success"
              />
            }
            label={row.isDisabled ? "ปิดใช้งาน" : "เปิดใช้งาน"}
            sx={{
              ".MuiFormControlLabel-label": {
                fontSize: "0.9rem",
              },
            }}
          />
        );
      },
    },
    // ✅ คอลัมน์ใหม่: เปลี่ยนรหัสผ่าน
    {
      field: "resetPassword",
      headerName: "เปลี่ยนรหัสผ่าน",
      width: 180,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Button
          variant="outlined"
          startIcon={<KeyIcon />}
          onClick={() => handleOpenReset(row)}
        >
          เปลี่ยนรหัส
        </Button>
      ),
    },
    {
      field: "actions",
      headerName: "",
      flex: 0,
      headerAlign: "center",
      align: "center",
      renderCell: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={`${id}-save`}
              icon={<SaveIcon />}
              label="Save"
              color="primary"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key={`${id}-cancel`}
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="error"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            key={`${id}-edit`}
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="primary"
          />,
          <GridActionsCellItem
            key={`${id}-delete`}
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="error"
          />,
        ];
      },
    },
  ];

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    const deletedRow = rows.find((row) => row.id === id);

    Swal.fire({
      customClass: {
        container: "my-swal",
      },
      title: "ยืนยันที่จะลบ?",
      text: "คุณจะไม่สามารถย้อนกลับสิ่งนี้ได้!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
    }).then((result) => {
      if (result.isConfirmed) {
        services
          .deleteUser(deletedRow.userId)
          .then((response) => {
            console.log("User deleted successfully:", response);
            toast.success("ลบสำเร็จ!");
            Swal.fire({
              customClass: {
                container: "my-swal",
              },
              title: "ลบแล้ว!",
              text: `${deletedRow.name} ${deletedRow.lastname} ถูกลบแล้ว`,
              icon: "success",
              confirmButtonText: "ตกลง",
            });
            setRows((prevRows) =>
              prevRows
                .filter((row) => row.branchId !== deletedRow.branchId)
                .map((item, index) => ({ ...item, id: index + 1 }))
            );
          })
          .catch((error) => {
            toast.error("ลบไม่สำเร็จ!");
            console.error("Error deleting user:", error);
          });
      }
    });
  };

  const handleToggleDisabled = (row) => {
    const updatedRow = { ...row, isDisabled: !row.isDisabled };

    services
      .updateUser(row.userId, { isDisabled: updatedRow.isDisabled })
      .then(() => {
        toast.success("อัปเดตสถานะเรียบร้อย");
        setRows((prevRows) =>
          prevRows.map((r) => (r.id === row.id ? updatedRow : r))
        );
      })
      .catch((err) => {
        toast.error("อัปเดตสถานะไม่สำเร็จ");
        console.error("Toggle disabled error:", err);
      });
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }

    const originalRow = revertData(editedRow);
    return originalRow; // คืนค่าผลลัพธ์เดิม
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const processRowUpdate = (newRow) => {
    console.log("newRow", newRow);

    // ตรวจสอบว่า roleId และ branchId ต้องไม่เป็นค่าว่าง
    if (!newRow.roleId || !newRow.branchId) {
      toast.error("กรุณาเลือกทั้งบทบาทและสาขา");
      return revertData(newRow); // ยกเลิกการแก้ไข
    }

    const payload = {
      name: newRow.name,
      lastname: newRow.lastname,
      branchId: newRow.branchId,
      roleId: newRow.roleId,
    };

    services
      .updateUser(newRow.userId, payload)
      .then((response) => {
        toast.success("แก้ไขสำเร็จ!");
        console.log("User updated successfully:", response);
        const updatedRow = { ...newRow, isNew: false };
        setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
      })
      .catch((error) => {
        toast.error("แก้ไขไม่สำเร็จ!");
        console.error("Error updating user:", error);
      });

    return newRow; // แสดงผล row เดิมต่อไป
  };

  const revertData = (newRow) => {
    const originalRow = rows.find((row) => row.id === newRow.id);
    if (!originalRow) return newRow;

    const revertedRow = {
      ...originalRow,
      branchId: originalRow.branchId_old ?? originalRow.branchId,
      roleId: originalRow.roleId_old ?? originalRow.roleId,
    };

    setRows((prevRows) =>
      prevRows.map((row) => (row.id === newRow.id ? revertedRow : row))
    );

    setRowModesModel({
      ...rowModesModel,
      [newRow.id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    console.log("Cancel the edit and revert to the original data");
    return revertedRow;
  };

  const handleBranchChange = (id, value) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === id
          ? { ...row, branchId: value, branchId_old: row.branchId }
          : row
      )
    );
  };

  const fetchData = useCallback(async () => {
    try {
      const shopResponse = await services.getShop();
      const shopData = shopResponse.data.data;
      console.log("Shop data fetched:", shopData);

      const usersWithIndex = shopData.users.map((item, index) => ({
        ...item,
        id: index + 1,
        userId: item.id,
      }));

      const updatedBranches = shopData.branches.map((branch, index) => ({
        ...branch,
        key: `branch-${index}`,
        value: branch?.id,
        label: branch?.name,
      }));

      const rolesResponse = await services.getRoles();
      const roleData = rolesResponse.data.data;
      console.log("roleData", roleData);
      setRoles(roleData || []);

      setBranches(updatedBranches);
      setRows(usersWithIndex);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [setRows, setBranches, setRoles, setLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <>
      <AppBreadcrumbs />

      <Toaster position="bottom-left" reverseOrder={false} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          my: 2,
        }}
      >
        <Typography component="h2" variant="h5">
          จัดการพนักงาน
        </Typography>

        <Button
          color="success"
          variant="contained"
          onClick={() => {
            setOpenDialog(true);
          }}
        >
          เพิ่มพนักงาน
        </Button>
      </Box>
      <Box sx={{ flex: 1, position: "relative" }}>
        <Box sx={{ position: "absolute", inset: 0 }}>
          <Paper>
            <DataGrid
              rows={rows}
              columns={columns}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={handleRowModesModelChange}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={processRowUpdate}
              loading={loading}
              slotProps={{
                toolbar: { setRows, setRowModesModel },
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
              }}
              initialState={{ pagination: { page: 0, pageSize: 50 } }}
              pageSizeOptions={[25, 50, 100]}
            />
          </Paper>
        </Box>
      </Box>

      <UserFormDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSuccess={() => {
          setLoading(true);
          fetchData();
        }}
      />

      {/* 🔐 Reset Password Dialog */}
      <Dialog
        open={openResetDialog}
        onClose={() => setOpenResetDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>เปลี่ยนรหัสผ่าน</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="ผู้ใช้"
              value={selectedUser?.username ?? ""}
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="รหัสผ่านใหม่"
              type={showPass ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!passwordError}
              helperText={passwordError || "ใช้ a–z, A–Z, 0–9 (8–32 ตัว)"}
              inputProps={{ maxLength: 32 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPass((v) => !v)}
                      edge="end"
                    >
                      {showPass ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="ยืนยันรหัสผ่าน"
              type={showConfirm ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              error={!!confirmError}
              helperText={confirmError || "พิมพ์ซ้ำให้ตรงกับรหัสผ่านใหม่"}
              inputProps={{ maxLength: 32 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm visibility"
                      onClick={() => setShowConfirm((v) => !v)}
                      edge="end"
                    >
                      {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenResetDialog(false)} color="inherit">
            ยกเลิก
          </Button>
          <Button
            onClick={handleSubmitReset}
            variant="contained"
            disabled={
              !password ||
              !confirm ||
              Boolean(passwordError) ||
              Boolean(confirmError)
            }
          >
            บันทึก
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Users;
